<template>
  <section id="about" class="py-16 bg-gray-900 relative overflow-hidden" ref="aboutSection">
    <!-- Background Decorations -->
    <div class="absolute inset-0 overflow-hidden">
      <div class="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-3xl"></div>
      <div class="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-primary/15 to-transparent rounded-full blur-3xl"></div>
      <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary/10 to-transparent rounded-full blur-3xl"></div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <!-- Part 1: Header + 3E Method -->
      <div class="mb-20">
        <!-- Header Section -->
        <div class="text-center mb-16" ref="aboutHeader">
          <div class="inline-flex items-center px-6 py-3 rounded-full bg-gray-800/80 backdrop-blur-md border border-gray-700/50 shadow-xl text-primary font-semibold text-sm mb-8 transform hover:scale-105 transition-all duration-300">
            <div class="w-2 h-2 bg-primary rounded-full mr-3 animate-pulse"></div>
            {{ $t('about.title') }}
          </div>
          <h2 class="text-3xl md:text-4xl font-bold text-white mb-8 leading-tight">
            {{ $t('about.subtitle') }}
          </h2>
          <p class="text-lg sm:text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-8 leading-relaxed text-justify">
            {{ $t('about.description') }}
          </p>
        </div>

        <!-- 3E Method Section -->
        <div class="max-w-6xl mx-auto">
          <div class="relative">
            <!-- Glassmorphism Card -->
            <div class="bg-gray-800/70 backdrop-blur-xl rounded-3xl p-10 shadow-2xl border border-gray-700/30 relative overflow-hidden">
              <!-- Subtle Pattern Overlay -->
              <div class="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50"></div>
              
              <div class="relative z-10">
                <h3 class="text-xl font-bold text-white mb-12 flex items-center justify-center">
                  <div class="relative">
                    <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center mr-6 shadow-lg transform hover:rotate-12 transition-transform duration-300">
                      <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <div class="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-bounce"></div>
                  </div>
                  {{ $t('about.method.title') }}
                </h3>
                
                <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div v-for="(method, index) in threeEMethods" :key="index" 
                       class="group method-point relative"
                       :ref="el => { if (el) methodPointsRefs[index] = el }">
                    <!-- 3D Card -->
                    <div class="bg-gradient-to-br from-gray-800/90 to-gray-800/70 backdrop-blur-md rounded-3xl p-8 shadow-xl border border-gray-700/40 transform hover:scale-105 hover:-translate-y-2 transition-all duration-500 hover:shadow-2xl">
                      <!-- Floating Number Badge -->
                      <div class="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-primary to-primary-dark rounded-2xl flex items-center justify-center shadow-lg transform group-hover:rotate-12 transition-transform duration-300">
                        <span class="text-white font-bold text-lg">{{ index + 1 }}</span>
                      </div>
                      
                      <!-- Icon Container -->
                      <div class="flex justify-center mb-6">
                        <div class="relative">
                          <div class="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                            <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center">
                              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="methodIcons[index]"></path>
                              </svg>
                            </div>
                          </div>
                          <!-- Glow Effect -->
                          <div class="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/20 to-transparent blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                      </div>
                      
                      <h4 class="font-bold text-white mb-4 text-xl text-center">{{ method.title }}</h4>
                      <p class="text-gray-300 leading-relaxed text-sm text-center">{{ method.description }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Part 2: Image + Key Features -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
        <!-- Left Column: Image & Stats -->
        <div class="relative flex items-center" ref="aboutImage">
          <!-- Main Image Container with 3D Effect -->
          <div class="relative w-full rounded-3xl overflow-hidden shadow-2xl transform hover:rotate-1 transition-transform duration-500">
            <!-- Gradient Overlay -->
            <div class="absolute inset-0 bg-gradient-to-br from-primary/30 via-primary/10 to-transparent z-10"></div>
            
            <!-- Image with Parallax Effect - Better aspect ratio -->
            <img :src="aboutImageSrc" 
                 alt="Heidelbell Team" 
                 class="w-full aspect-[4/5] object-cover transform hover:scale-110 transition-transform duration-700">
            
            <!-- Floating Achievement Badge -->
            <div class="absolute top-6 right-6 bg-gradient-to-r from-primary to-primary-dark text-white rounded-2xl p-4 shadow-xl transform hover:scale-105 transition-all duration-300">
              <div class="text-2xl font-bold">{{ $t('about.achievement.number') }}</div>
              <div class="text-sm opacity-90">{{ $t('about.achievement.label') }}</div>
            </div>
          </div>

          <!-- Enhanced Decorative Elements -->
          <div class="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-primary/30 to-transparent rounded-full blur-2xl animate-pulse"></div>
          <div class="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-2xl animate-pulse delay-1000"></div>
        </div>

        <!-- Right Column: Key Features -->
        <div ref="aboutContent" class="flex items-center">
          <div class="bg-gray-800/80 backdrop-blur-xl rounded-3xl p-8 lg:p-10 shadow-2xl border border-gray-700/30 relative overflow-hidden w-full">
            <!-- Subtle Pattern -->
            <div class="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent"></div>
            
            <div class="relative z-10">
              <h4 class="text-xl lg:text-2xl font-bold text-white mb-6 lg:mb-8 flex items-center">
                <div class="relative mr-3 lg:mr-4">
                  <svg class="w-6 h-6 lg:w-8 lg:h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                  </svg>
                  <div class="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-ping"></div>
                </div>
                <span class="font-heading">{{ $t('about.whyChooseTitle') }}</span>
              </h4>
              
              <div class="space-y-4 lg:space-y-6">
                <div v-for="(point, index) in aboutPoints" :key="index" 
                     class="group about-point relative"
                     :ref="el => { if (el) aboutPointsRefs[index] = el }">
                  <div class="bg-gradient-to-r from-gray-800/60 to-gray-800/40 backdrop-blur-md rounded-2xl p-5 lg:p-6 shadow-lg border border-gray-700/30 transform hover:scale-105 hover:-translate-y-1 transition-all duration-300 hover:shadow-xl">
                    <div class="flex items-start">
                      <div class="relative mr-3 lg:mr-4 flex-shrink-0">
                        <div class="w-4 h-4 bg-gradient-to-r from-primary to-primary-dark rounded-full mt-1.5 lg:mt-2 group-hover:scale-125 transition-transform duration-300"></div>
                        <div class="absolute inset-0 bg-primary/30 rounded-full animate-ping"></div>
                      </div>
                      <span class="text-gray-100 font-medium lg:font-semibold text-base lg:text-lg leading-relaxed">{{ point }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import enTranslations from '../i18n/en'
import deTranslations from '../i18n/de'
import { textReveal, staggerChildren } from '../utils/animations'
import aboutImageSrc from '../assets/person-working-html-computer.jpg'

const { t, locale } = useI18n()
const aboutSection = ref(null)
const aboutHeader = ref(null)
const aboutContent = ref(null)
const aboutImage = ref(null)
const aboutPointsRefs = ref([])
const methodPointsRefs = ref([])

// Helper function to get array translations
const getArrayTranslation = (key) => {
  const keys = key.split('.')
  const currentLocale = locale.value || 'en'
  const messages = currentLocale === 'en' ? enTranslations : deTranslations
  
  let value = messages
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k]
    } else {
      console.warn(`Translation key not found: ${key} at ${k}`)
      return []
    }
  }
  return Array.isArray(value) ? value : []
}

const aboutPoints = computed(() => getArrayTranslation('about.whyChoosePoints'))

const threeEMethods = computed(() => [
  {
    title: 'Evaluate',
    description: t('about.method.evaluate')
  },
  {
    title: 'Execute', 
    description: t('about.method.execute')
  },
  {
    title: 'Elevate',
    description: t('about.method.elevate')
  }
])

const methodIcons = [
  'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  'M13 10V3L4 14h7v7l9-11h-7z',
  'M7 11l5-5m0 0l5 5m-5-5v12'
]

onMounted(() => {
  const aboutObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        textReveal(aboutHeader.value, 0)
        textReveal(aboutContent.value, 200)
        textReveal(aboutImage.value, 400)
        staggerChildren(aboutSection.value, '.about-point', 100)
        staggerChildren(aboutSection.value, '.method-point', 150)
      }
    })
  }, { threshold: 0.2 })
  aboutObserver.observe(aboutSection.value)
})
</script>

<style scoped>
.about-point, .method-point {
  @apply opacity-0 transform translate-y-8;
  animation: fadeInUp 0.8s ease-out forwards;
}

@keyframes fadeInUp {
  to {
    @apply opacity-100 transform translate-y-0;
  }
}

.method-point:nth-child(1) { animation-delay: 0.1s; }
.method-point:nth-child(2) { animation-delay: 0.2s; }
.method-point:nth-child(3) { animation-delay: 0.3s; }

.about-point:nth-child(1) { animation-delay: 0.1s; }
.about-point:nth-child(2) { animation-delay: 0.2s; }
.about-point:nth-child(3) { animation-delay: 0.3s; }
.about-point:nth-child(4) { animation-delay: 0.4s; }

/* Custom animations */
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

/* Glassmorphism enhancement */
.backdrop-blur-xl {
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}
</style> 